import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, ShieldCheck, Rocket, HeartHandshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/useLanguage";

const serviceDefs = [
  { key: "strategy", icon: Target, link: "/services#strategy" },
  { key: "governance", icon: ShieldCheck, link: "/services#governance" },
  { key: "implementation", icon: Rocket, link: "/services#implementation" },
  { key: "support", icon: HeartHandshake, link: "/services#support" },
] as const;

const Services = () => {
  const { t } = useTranslation();
  const { localize } = useLanguage();

  return (
    <Section
      title={<>{t("services.titlePre")} <span className="text-gradient-brand">{t("services.titleAccent")}</span></>}
      surface="alt"
      grid
    >
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {serviceDefs.map((service, index) => {
          const IconComponent = service.icon;
          return (
            <Reveal key={service.key} delay={index * 100}>
              <Link
                to={localize(service.link)}
                className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
              >
                <Card
                  className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-glow-card border-border/60 hover:border-primary/40 bg-background/90 backdrop-blur-sm h-full"
                >
                  <div
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-px bg-gradient-brand opacity-0 group-hover:opacity-100 transition-opacity"
                  />

                  <CardHeader className="p-5 md:p-6">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-xl bg-gradient-brand transition-all duration-300 group-hover:scale-110 group-hover:shadow-glow-primary">
                        <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg md:text-xl flex items-center gap-2">
                          {t(`services.items.${service.key}.title`)}
                          <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-1" />
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t(`services.items.${service.key}.tagline`)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="text-center mt-8 md:mt-10" delay={150}>
        <Button variant="secondary" size="lg" asChild>
          <Link to={localize("/services")} className="inline-flex items-center gap-2">
            {t("services.exploreAll")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </Reveal>
    </Section>
  );
};

export default Services;
